import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { QuoteButton } from "./QuoteButton";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ClipboardCheck, Sparkles, BookOpen, MapPin } from "lucide-react";
import { toast } from "sonner";

function GradeBanner({ ev }) {
  return (
    <div className="rounded-2xl integrity-gradient p-6 text-white" data-testid="grade-banner">
      <div className="text-xs uppercase tracking-wide opacity-80">Estimated grade</div>
      <div className="font-display text-3xl font-extrabold">{ev.grade_label || `${ev.overall_range?.low_pct}–${ev.overall_range?.high_pct}%`}</div>
      <div className="mt-1 text-sm opacity-90">Band: {ev.rubric_band} · <span className="italic">estimated — your marker decides the final grade.</span></div>
    </div>
  );
}

function RubricTable({ ev }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <h4 className="mb-3 font-display font-semibold">Criterion scores</h4>
      <div className="space-y-3">
        {ev.criteria?.map((c, i) => {
          const pct = c.max ? Math.round((c.score / c.max) * 100) : 0;
          return (
            <div key={i} data-testid={`criterion-${i}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{c.criterion}</span>
                <span className="font-mono font-semibold text-[#0B5D3B] dark:text-[#25D366]">{c.score}/{c.max}</span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-[#0B5D3B] transition-all duration-500 dark:bg-[#25D366]" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{c.reason}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Corrections({ assignment, ev, evalId, fixed, setFixed, activePara, setActivePara }) {
  const toggle = async (n) => {
    try {
      const { data } = await api.patch(`/assignments/${assignment.id}/evaluations/${evalId}/correction/${n}`);
      setFixed(data.corrections_fixed);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const total = ev.corrections?.length || 0;
  return (
    <div className="space-y-3" data-testid="corrections-list">
      <div className="flex items-center justify-between rounded-xl bg-accent px-4 py-2 text-sm font-medium">
        <span>{fixed.length} of {total} corrections fixed</span>
        <span className="text-xs text-muted-foreground">Upload a revised draft to see a new estimate</span>
      </div>
      {ev.corrections?.map((c) => {
        const isFixed = fixed.includes(c.n);
        return (
          <div key={c.n} data-testid={`correction-${c.n}`}
            className={`rounded-2xl border bg-card p-5 transition-colors duration-200 ${isFixed ? "border-[#059669]/40 opacity-70" : "border-border"} ${activePara === c.paragraph_id ? "ring-2 ring-[#0B5D3B]/40" : ""}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0B5D3B] text-xs font-bold text-white dark:bg-[#25D366] dark:text-black">{c.n}</span>
                {c.paragraph_id
                  ? <button onClick={() => setActivePara(c.paragraph_id)} className="flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 font-mono text-xs font-semibold" data-testid={`correction-anchor-${c.n}`}><MapPin className="h-3 w-3" />{c.paragraph_id}</button>
                  : <span className="rounded-md bg-accent px-2 py-0.5 font-mono text-xs font-semibold">General</span>}
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
                <Checkbox checked={isFixed} onCheckedChange={() => toggle(c.n)} data-testid={`correction-fix-${c.n}`} /> Mark as fixed
              </label>
            </div>
            {c.quote && <p className="mt-3 border-l-2 border-border pl-3 text-xs italic text-muted-foreground">"{c.quote}"</p>}
            <div className="mt-3 space-y-2 text-sm">
              <p><span className="font-semibold">Problem:</span> {c.problem}</p>
              <p className="text-muted-foreground"><span className="font-semibold text-foreground">Why it costs marks:</span> {c.why_marks_lost}</p>
              <p className="rounded-lg bg-[#E8F5EE] p-2.5 dark:bg-emerald-950/50"><span className="font-semibold">How to fix it:</span> {c.how_to_fix}</p>
            </div>
            {c.term_definitions?.length > 0 && (
              <div className="mt-3 rounded-lg border border-border p-2.5 text-xs">
                <div className="mb-1 flex items-center gap-1 font-semibold text-muted-foreground"><BookOpen className="h-3 w-3" />Plain English</div>
                {c.term_definitions.map((t, j) => <p key={j}><strong>{t.term}:</strong> {t.definition}</p>)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DraftPanel({ paragraphs, activePara, setActivePara, corrected }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="draft-panel">
      <h4 className="mb-3 font-display font-semibold">Your draft</h4>
      <div className="space-y-3">
        {paragraphs?.map((p) => (
          <div key={p.id} id={`para-${p.id}`} onClick={() => setActivePara(p.id)} data-testid={`para-${p.id}`}
            className={`cursor-pointer rounded-lg px-3 py-2 text-sm transition-colors duration-200 ${activePara === p.id ? "para-highlight" : "hover:bg-accent/60"}`}>
            <span className="mr-2 font-mono text-xs font-semibold text-[#0B5D3B] dark:text-[#25D366]">[{p.id}]{corrected.has(p.id) ? " ●" : ""}</span>
            {p.text}
          </div>
        ))}
      </div>
    </div>
  );
}

export function StepEvaluate({ assignment, reload }) {
  const evals = assignment.evaluations || [];
  const latest = evals[0];
  const [evalId, setEvalId] = useState(latest?.id);
  const current = evals.find((e) => e.id === evalId) || latest;
  const [fixed, setFixed] = useState(current?.corrections_fixed || []);
  const [activePara, setActivePara] = useState(null);

  const onDone = () => { reload(); };

  if (!assignment.draft_text) {
    return <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground" data-testid="evaluate-no-draft">Add your draft in the Upload step, then evaluate it here.</div>;
  }
  if (!assignment.confirmed_requirements) {
    return <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground" data-testid="evaluate-locked">Confirm your brief details in the Check step first.</div>;
  }

  if (!current) {
    return (
      <div className="flex flex-col items-center rounded-2xl border border-border bg-card p-10 text-center" data-testid="evaluate-generate">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E8F5EE] text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]"><ClipboardCheck className="h-6 w-6" /></div>
        <h3 className="mt-4 font-display text-xl font-semibold">Evaluate your draft</h3>
        <p className="mt-1 max-w-md text-muted-foreground">Honest rubric scores, an estimated grade range, your strengths, and numbered fixes anchored to your paragraphs.</p>
        <div className="mt-6"><QuoteButton assignmentId={assignment.id} action="evaluate" label="Evaluate draft" icon={ClipboardCheck} onDone={onDone} testid="evaluate-btn" /></div>
      </div>
    );
  }

  const ev = current.json;
  const fixedSet = new Set(fixed);
  const correctedParas = new Set(ev.corrections?.filter((c) => fixedSet.has(c.n) && c.paragraph_id).map((c) => c.paragraph_id));

  return (
    <div className="space-y-5" data-testid="step-evaluate">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <GradeBanner ev={ev} />
      </div>

      {evals.length > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Version:</span>
          {evals.map((e, i) => (
            <button key={e.id} onClick={() => { setEvalId(e.id); setFixed(e.corrections_fixed || []); }} data-testid={`eval-version-${i}`}
              className={`rounded-lg px-2.5 py-1 font-mono text-xs font-semibold ${e.id === evalId ? "bg-[#0B5D3B] text-white dark:bg-[#25D366] dark:text-black" : "bg-accent"}`}>
              v{e.draft_version} · {e.json.grade_label?.split(",")[0] || `${e.json.overall_range?.low_pct}%`}
            </button>
          ))}
        </div>
      )}

      {ev.strengths?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-2 flex items-center gap-2 font-display font-semibold"><Sparkles className="h-4 w-4 text-[#059669]" />Strengths</div>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">{ev.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}

      <RubricTable ev={ev} />

      {/* desktop split */}
      <div className="hidden gap-5 lg:grid lg:grid-cols-2">
        <DraftPanel paragraphs={assignment.draft_paragraphs} activePara={activePara} setActivePara={setActivePara} corrected={correctedParas} />
        <Corrections assignment={assignment} ev={ev} evalId={current.id} fixed={fixed} setFixed={setFixed} activePara={activePara} setActivePara={setActivePara} />
      </div>

      {/* mobile tabs */}
      <div className="lg:hidden">
        <Tabs defaultValue="corrections">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="corrections" data-testid="mobile-tab-corrections">Corrections</TabsTrigger>
            <TabsTrigger value="draft" data-testid="mobile-tab-draft">Draft</TabsTrigger>
          </TabsList>
          <TabsContent value="corrections" className="mt-4">
            <Corrections assignment={assignment} ev={ev} evalId={current.id} fixed={fixed} setFixed={setFixed} activePara={activePara} setActivePara={setActivePara} />
          </TabsContent>
          <TabsContent value="draft" className="mt-4">
            <DraftPanel paragraphs={assignment.draft_paragraphs} activePara={activePara} setActivePara={setActivePara} corrected={correctedParas} />
          </TabsContent>
        </Tabs>
      </div>

      <div className="flex items-center justify-end gap-3">
        <span className="text-sm text-muted-foreground">Revised your draft? Re-upload it, then:</span>
        <QuoteButton assignmentId={assignment.id} action="evaluate" label="Re-evaluate" icon={ClipboardCheck} onDone={onDone} testid="reevaluate-btn" />
      </div>
    </div>
  );
}
